<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- 遮罩层 -->
        <div class="absolute inset-0 bg-black/50" @click="$emit('close')"></div>
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" role="dialog">
          <!-- 顶部颜色条 -->
          <div :class="['h-2 rounded-t-2xl', eventHeaderClass]"></div>
          <div class="p-6">
            <!-- 加载状态 -->
            <div v-if="loading" class="flex items-center justify-center py-8">
              <svg class="w-6 h-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <template v-else-if="event">
              <!-- 类型标签、全天标识、被邀请状态 -->
              <div class="flex items-center gap-2 mb-3 flex-wrap">
                <span :class="['px-2 py-0.5 rounded text-xs font-medium', typeBadgeClass]">
                  {{ typeLabel }}
                </span>
                <span v-if="event.isAllDay === 1" class="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">全天</span>
                <span v-if="!event.isOwner" :class="['px-2 py-0.5 rounded text-xs', attendeeStatusClass]">
                  {{ attendeeStatusLabel }}
                </span>
              </div>

              <!-- 标题 -->
              <h3 class="text-xl font-semibold text-slate-900 mb-4">{{ event.title }}</h3>

              <!-- 时间信息 -->
              <div class="flex items-center gap-2 text-sm text-slate-600 mb-4">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>
                  {{ event.date }} {{ event.time }}
                  <template v-if="event.endTime && event.endTime !== event.time">
                    - {{ event.endTime }}
                  </template>
                </span>
              </div>

              <div class="space-y-3 text-sm">
                <!-- 地点 -->
                <div v-if="event.location" class="flex items-start gap-2 text-slate-600">
                  <svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <span>{{ event.location }}</span>
                </div>

                <!-- 参与人 -->
                <div v-if="event.attendeeList && event.attendeeList.length > 0" class="flex items-start gap-2 text-slate-600">
                  <svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                  </svg>
                  <div class="flex flex-wrap gap-3">
                    <div
                      v-for="(person, index) in event.attendeeList"
                      :key="index"
                      class="flex items-center gap-1.5"
                    >
                      <!-- 头像容器 -->
                      <div class="relative">
                        <img
                          v-if="person.avatar"
                          :src="person.avatar"
                          :alt="person.userName"
                          class="w-6 h-6 rounded-full"
                        />
                        <span v-else class="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs text-slate-600">
                          {{ (person.userName || '?').charAt(0) }}
                        </span>
                        <!-- 状态图标 -->
                        <span v-if="person.status === 1" class="absolute -top-1.5 -right-1.5 text-amber-500" title="待定">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </span>
                        <span v-else-if="person.status === 2" class="absolute -top-1.5 -right-1.5 text-green-500" title="已接受">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                          </svg>
                        </span>
                        <span v-else-if="person.status === 3" class="absolute -top-1.5 -right-1.5 text-red-500" title="已拒绝">
                          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </span>
                      </div>
                      <span class="text-slate-600 text-sm">{{ person.userName }}</span>
                    </div>
                  </div>
                </div>
                <span v-else class="text-slate-400">无</span>

                <!-- 描述 -->
                <p v-if="event.description" class="text-slate-600">{{ event.description }}</p>

                <!-- CRM关联信息 -->
                <div v-if="event.customerName || event.opportunityName || event.contractName || event.projectName" class="pt-2 border-t border-slate-100">
                  <p class="text-xs font-medium text-slate-500 mb-2">关联信息</p>
                  <div class="space-y-1">
                    <div v-if="event.customerName" class="flex items-center gap-2 text-slate-600">
                      <span class="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">客户</span>
                      <span>{{ event.customerName }}</span>
                    </div>
                    <div v-if="event.opportunityName" class="flex items-center gap-2 text-slate-600">
                      <span class="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">商机</span>
                      <span>{{ event.opportunityName }}</span>
                    </div>
                    <div v-if="event.contractName" class="flex items-center gap-2 text-slate-600">
                      <span class="px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-xs">合同</span>
                      <span>{{ event.contractName }}</span>
                    </div>
                    <div v-if="event.projectName" class="flex items-center gap-2 text-slate-600">
                      <span class="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-xs">项目</span>
                      <span>{{ event.projectName }}</span>
                    </div>
                  </div>
                </div>

                <!-- 创建/更新时间 -->
                <div v-if="event.createdAt" class="pt-2 border-t border-slate-100 text-xs text-slate-400">
                  <p>创建于 {{ new Date(event.createdAt).toLocaleString('zh-CN') }}</p>
                  <p v-if="event.updatedAt && event.updatedAt !== event.createdAt">
                    更新于 {{ new Date(event.updatedAt).toLocaleString('zh-CN') }}
                  </p>
                </div>
              </div>

              <!-- 操作按钮区域 -->
              <div class="flex gap-3 mt-6">
                <!-- 待定邀请的接受/拒绝按钮 -->
                <template v-if="!event.isOwner && event.attendeeStatus === 1">
                  <button
                    @click="$emit('accept')"
                    type="button"
                    class="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    接受
                  </button>
                  <button
                    @click="$emit('decline')"
                    type="button"
                    class="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                    拒绝
                  </button>
                </template>
                <!-- 非待定邀请的其他操作 -->
                <template v-else>
                  <!-- 编辑按钮（拥有者） -->
                  <button
                    v-if="event.isOwner && showEdit"
                    @click="$emit('edit')"
                    type="button"
                    class="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    编辑
                  </button>
                  <!-- 取消参与按钮（非拥有者且已接受） -->
                  <button
                    v-else-if="!event.isOwner && event.attendeeStatus === 2 && showCancel"
                    @click="$emit('cancel')"
                    type="button"
                    class="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    取消参与
                  </button>
                  <!-- 关闭/我知道了按钮 -->
                  <button
                    @click="$emit('close')"
                    type="button"
                    :class="event.isOwner && showEdit ? 'px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors' : 'flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors'"
                  >
                    {{ event.isOwner && showEdit ? '关闭' : '我知道了' }}
                  </button>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

defineEmits(['close', 'edit', 'accept', 'decline', 'cancel'])
/* eslint-disable no-unused-vars */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  event: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  },
  showEdit: {
    type: Boolean,
    default: true
  },
  showCancel: {
    type: Boolean,
    default: true
  }
})

// 日程类型映射
const typeMap = {
  MEETING: { value: 'meeting', label: '会议', color: '#3B82F6' },
  TASK: { value: 'task', label: '任务', color: '#F59E0B' },
  CALL: { value: 'call', label: '电话', color: '#10B981' },
  DEADLINE: { value: 'deadline', label: '截止', color: '#EF4444' },
  OTHER: { value: 'other', label: '其他', color: '#6B7280' },
  meeting: { value: 'meeting', label: '会议', color: '#3B82F6' },
  task: { value: 'task', label: '任务', color: '#F59E0B' },
  call: { value: 'call', label: '电话', color: '#10B981' },
  deadline: { value: 'deadline', label: '截止', color: '#EF4444' },
  other: { value: 'other', label: '其他', color: '#6B7280' }
}

// 获取事件头部颜色类
const eventHeaderClass = computed(() => {
  const typeInfo = typeMap[props.event?.typeBackend || props.event?.type] || typeMap.OTHER
  return typeInfo.color
})

// 获取类型标签
const typeLabel = computed(() => {
  const typeInfo = typeMap[props.event?.typeBackend || props.event?.type] || typeMap.OTHER
  return typeInfo.label
})

// 获取类型徽章类
const typeBadgeClass = computed(() => {
  const typeInfo = typeMap[props.event?.typeBackend || props.event?.type] || typeMap.OTHER
  const color = typeInfo.color
  const colorMap = {
    '#3B82F6': 'bg-blue-100 text-blue-700',
    '#F59E0B': 'bg-amber-100 text-amber-700',
    '#10B981': 'bg-green-100 text-green-700',
    '#EF4444': 'bg-red-100 text-red-700',
    '#6B7280': 'bg-slate-100 text-slate-700'
  }
  return colorMap[color] || 'bg-slate-100 text-slate-700'
})

// 获取参与状态标签
const attendeeStatusMap = {
  1: { label: '待定', class: 'bg-amber-100 text-amber-700' },
  2: { label: '已接受', class: 'bg-green-100 text-green-700' },
  3: { label: '已拒绝', class: 'bg-red-100 text-red-700' }
}

const attendeeStatusLabel = computed(() => {
  return attendeeStatusMap[props.event?.attendeeStatus]?.label || '未知'
})

const attendeeStatusClass = computed(() => {
  return attendeeStatusMap[props.event?.attendeeStatus]?.class || 'bg-slate-100 text-slate-700'
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
